import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';

interface Props {
  showDisclaimerModal: boolean;
  setShowDisclaimerModal: (show: boolean) => void;
  onModalClose: () => void;
}
export const DisclaimerModal: React.FC<Props> = ({ showDisclaimerModal, setShowDisclaimerModal, onModalClose }) => {
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const handleCloseClick = () => {
    setIsAcknowledged(true);
    onModalClose();
  };

  return (
    <Transition.Root show={showDisclaimerModal} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[100]"
        onClose={() => {
          if (isAcknowledged) {
            setShowDisclaimerModal(false);
          }
        }}
      >
        {/* Opaque overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[#B2B2B2] bg-opacity-100 transition-opacity  " />
        </Transition.Child>
        {/* Modal content */}
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <Dialog.Panel className="relative flex border-t-4 border-t-[#A100FF] tablet:w-[882px] h-auto tablet:h-[519px] transform flex-col bg-gamma-modal-bg font-graphik text-black transition-all">
                <div className="flex justify-between items-start px-6 pt-8 sm:p-6">
                  <Dialog.Title className="font-graphik text-[#000000] text-left text-[20px] tablet:text-[20px] not-italic font-medium tablet:font-medium leading-[20px] tablet:leading-[28px]">
                    General Legal Disclaimer on Confidentiality and Gen AI usage
                  </Dialog.Title>
                  {/* Close icon here */}
                  <button className="focus:outline-none" onClick={() => handleCloseClick()}>
                    <img src="/icons/Close.svg" alt="Close Icon" className="w-4 h-4" />
                  </button>
                </div>
                <div className="relative flex transform flex-col bg-[var(--Surface-card-bg, #FFF)] shadow-md pt-[25px] pl-[34px] pb-[38px] gap-5 Scrollbar">
                  <Dialog.Description className="font-graphik text-[#000000] text-sm not-italic font-normal leading-[20px] mr-6">
                    {/* <div className="c-modal__header c-legal__header font-bold text-lg ">
                      General Legal Disclaimer on Confidentiality and Gen AI usage
                    </div> */}
                    <div className="c-modal__body c-legal__body -customscroll">
                      <div className="c-legal__title text-small mt-6 font-bold">Confidentiality Disclaimer</div>
                      <p className="c-legal__desc -confidential mt-1 ">
                        You are using an Accenture proprietary tool (&lsquo;Gen AI on the Console&rsquo; including Gen
                        AI applications included in &lsquo;Gen AI on the Console&rsquo;) a functionality of the
                        Accenture Reinvention Console). Information in the tool can only be shared with those authorized
                        to receive it. &lsquo;Internal Use&rsquo; and &lsquo;Internal Use Restricted&rsquo; content in
                        Solution cannot be shared with clients. You may share &lsquo;Unrestricted&rsquo; content.
                        &lsquo;External Use - Restricted&rsquo; content can only be shared with Client and/or
                        prospective Client in certain circumstances and needs to be confirmed with Content Owner or
                        Solution Owner for an evaluation of requirements prior to sharing content (e.g. sign an NDA).{' '}
                        <br />
                        <p className="mt-2">
                          By accessing the tool and information in the tool, you certify that you are allowed to share
                          the content or data (e.g. you have checked with Client Account Lead, Project Lead or
                          responsible MD as required) and obligated to adhere to the terms of use for the &nbsp;
                        </p>
                        <a
                          href="https://reinventionconsole.accenture.com/console"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-hidden="true"
                          className="c-legal__privacy underline text-blue-700"
                        >
                          Accenture Reinvention Console,
                        </a>{' '}
                        as well as all relevant Accenture Policies, including&nbsp;
                        <a
                          href="https://policies.accenture.com/policy/0069"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-hidden="true"
                          className="c-legal__privacy underline text-blue-700"
                        >
                          Policy 69 - Confidentiality,
                        </a>
                        &nbsp;
                        <a
                          href="https://policies.accenture.com/policy/1431"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-hidden="true"
                          className="c-legal__privacy underline text-blue-700"
                        >
                          Policy 1431 - Data Management,
                        </a>
                        &nbsp;
                        <a
                          href="https://policies.accenture.com/policy/1457"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-hidden="true"
                          className="c-legal__privacy underline text-blue-700"
                        >
                          Policy 1457 - Information Security Management,
                        </a>
                        &nbsp;
                        <a
                          href="https://policies.accenture.com/policy/0090"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-hidden="true"
                          className="c-legal__privacy underline text-blue-700"
                        >
                          Data Privacy Policy 0090
                        </a>
                        &nbsp;and&nbsp;
                        <a
                          href="https://policies.accenture.com/policy/0057"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-hidden="true"
                          className="c-legal__privacy underline text-blue-700"
                        >
                          Policy 57 - Acceptable Use of Information.
                        </a>
                        &nbsp;&nbsp;
                      </p>
                      <div className="c-legal__title text-small mt-3 font-bold">Generative AI Disclaimer</div>
                      <p className="c-legal__desc -profit mt-2">
                        You understand that the &lsquo;Gen AI on the Console&rsquo; including the Gen AI applications
                        included in &lsquo;Gen AI on the Console&rsquo; tool may produce content generated by AI and
                        that such AI generated content may not be unique, accurate, or reliable. You will be given
                        notice of any AI generated content. You must review and use discretion before relying on,
                        publishing, or otherwise using any AI generated content to ensure that it is suitable for your
                        requirements. You must comply with all applicable confidentiality obligations, content sharing
                        restrictions, and all relevant Accenture policies as mentioned above and the Generative AI Legal
                        Playbook - Navigating the GenAI and LLM Landscape (accenture.com).
                      </p>
                    </div>
                  </Dialog.Description>
                </div>
                <div className="flex flex-row justify-end gap-8 mr-6 mb-6 pt-[20px]">
                  <button
                    className="flex items-start px-4 py-[11px] bg-[#000]"
                    onClick={() => {
                      handleCloseClick();
                    }}
                  >
                    <p className="font-graphik text-white text-center font-400 text-[14px]"> Acknowledge</p>
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
